import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Sparkles, Save, Loader2, Plus, Trash2, GripVertical, Play, Eye,
  Users, Briefcase, HandCoins, LayoutDashboard, MessageSquare, 
  Calendar, FolderKanban, Wallet, FileText, Clock, BookOpen, 
  Bell, FileSignature, Target, Star, Settings
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const ICON_OPTIONS = [
  { value: 'LayoutDashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'MessageSquare', label: 'Messages', icon: MessageSquare },
  { value: 'Calendar', label: 'Calendrier', icon: Calendar },
  { value: 'FolderKanban', label: 'Projets', icon: FolderKanban },
  { value: 'Wallet', label: 'Portefeuille', icon: Wallet },
  { value: 'FileText', label: 'Documents', icon: FileText },
  { value: 'Clock', label: 'Temps', icon: Clock },
  { value: 'BookOpen', label: 'Tutoriels', icon: BookOpen },
  { value: 'Bell', label: 'Notifications', icon: Bell },
  { value: 'FileSignature', label: 'Contrats', icon: FileSignature },
  { value: 'Target', label: 'Objectifs', icon: Target },
  { value: 'HandCoins', label: 'Commissions', icon: HandCoins },
  { value: 'Users', label: 'Équipe', icon: Users },
  { value: 'Sparkles', label: 'Bienvenue', icon: Sparkles },
  { value: 'Star', label: 'Étoile', icon: Star },
  { value: 'Settings', label: 'Réglages', icon: Settings },
];

const DEFAULT_STEPS = {
  client: [
    { icon: 'LayoutDashboard', title: "Votre Tableau de Bord", description: "Bienvenue ! Ici vous voyez en un coup d'œil vos projets en cours, votre solde d'heures et les dernières actualités.", videoUrl: '' },
    { icon: 'FolderKanban', title: "Vos Projets", description: "Consultez tous vos projets, l'avancement, validez les livrables et échangez avec l'équipe.", videoUrl: '' },
    { icon: 'MessageSquare', title: "Messagerie", description: "Communiquez facilement avec l'agence. Posez vos questions et suivez vos conversations.", videoUrl: '' },
    { icon: 'Wallet', title: "Votre Portefeuille", description: "Gérez votre crédit d'heures. Rechargez, consultez vos consommations et factures.", videoUrl: '' },
    { icon: 'Calendar', title: "Rendez-vous", description: "Planifiez et gérez vos rendez-vous avec l'agence.", videoUrl: '' },
  ],
  team_member: [
    { icon: 'LayoutDashboard', title: "Votre Espace de Travail", description: "Votre tableau de bord avec vos tâches du jour et projets en cours.", videoUrl: '' },
    { icon: 'Calendar', title: "Votre Planning", description: "Consultez votre emploi du temps et rendez-vous.", videoUrl: '' },
    { icon: 'FolderKanban', title: "Vos Projets", description: "Accédez aux projets assignés et mettez à jour vos tâches.", videoUrl: '' },
    { icon: 'Clock', title: "Suivi du Temps", description: "Lancez le chronomètre pour tracker vos heures de travail.", videoUrl: '' },
    { icon: 'FileText', title: "Rapports Mensuels", description: "Déclarez vos heures travaillées chaque mois.", videoUrl: '' },
    { icon: 'FileSignature', title: "Vos Contrats", description: "Signez votre contrat mensuel pour débloquer votre accès.", videoUrl: '' },
  ],
  affiliate: [
    { icon: 'Sparkles', title: "Bienvenue Apporteur !", description: "Recommandez des clients et gagnez des commissions.", videoUrl: '' },
    { icon: 'Target', title: "Ajouter des Leads", description: "Saisissez les prospects que vous nous recommandez.", videoUrl: '' },
    { icon: 'HandCoins', title: "Vos Commissions", description: "Suivez vos gains : 10% du premier projet payé.", videoUrl: '' },
    { icon: 'FileSignature', title: "Votre Contrat", description: "Consultez les conditions de partenariat.", videoUrl: '' },
  ]
};

const GuidedTourSettingsPage = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    tours: {
      client: [],
      team_member: [],
      affiliate: []
    }
  });
  const [activeTab, setActiveTab] = useState('client');
  const [editingStep, setEditingStep] = useState(null);
  const [showStepModal, setShowStepModal] = useState(false);
  const [previewRole, setPreviewRole] = useState(null);
  const [previewStep, setPreviewStep] = useState(0);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings/guided-tour');
      if (res.data) {
        setSettings({
          enabled: res.data.enabled !== false,
          tours: {
            client: res.data.tours?.client || DEFAULT_STEPS.client,
            team_member: res.data.tours?.team_member || DEFAULT_STEPS.team_member,
            affiliate: res.data.tours?.affiliate || DEFAULT_STEPS.affiliate
          }
        });
      } else {
        // Use defaults
        setSettings({
          enabled: true,
          tours: DEFAULT_STEPS
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettings({
        enabled: true,
        tours: DEFAULT_STEPS
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/guided-tour', settings);
      toast.success('Paramètres de la visite guidée enregistrés');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const addStep = (role) => {
    setEditingStep({
      icon: 'Sparkles',
      title: '',
      description: '',
      videoUrl: '',
      isNew: true,
      role
    });
    setShowStepModal(true);
  };

  const editStep = (role, index) => {
    setEditingStep({
      ...settings.tours[role][index],
      index,
      role,
      isNew: false
    });
    setShowStepModal(true);
  };

  const saveStep = () => {
    if (!editingStep.title || !editingStep.description) {
      toast.error('Veuillez remplir le titre et la description');
      return;
    }

    const newTours = { ...settings.tours };
    const stepData = {
      icon: editingStep.icon,
      title: editingStep.title,
      description: editingStep.description,
      videoUrl: editingStep.videoUrl || ''
    };

    if (editingStep.isNew) {
      newTours[editingStep.role] = [...newTours[editingStep.role], stepData];
    } else {
      newTours[editingStep.role][editingStep.index] = stepData;
    }

    setSettings({ ...settings, tours: newTours });
    setShowStepModal(false);
    setEditingStep(null);
  };

  const deleteStep = (role, index) => {
    const newTours = { ...settings.tours };
    newTours[role] = newTours[role].filter((_, i) => i !== index);
    setSettings({ ...settings, tours: newTours });
  };

  const moveStep = (role, index, direction) => {
    const newTours = { ...settings.tours };
    const steps = [...newTours[role]];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= steps.length) return;
    
    [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
    newTours[role] = steps;
    setSettings({ ...settings, tours: newTours });
  };

  const resetToDefaults = (role) => {
    const newTours = { ...settings.tours };
    newTours[role] = DEFAULT_STEPS[role];
    setSettings({ ...settings, tours: newTours });
    toast.success('Étapes réinitialisées par défaut');
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'client': return 'Espace Client';
      case 'team_member': return 'Espace Membre / Sous-traitant';
      case 'affiliate': return 'Espace Apporteur d\'affaires';
      default: return role;
    }
  };

  const getIconComponent = (iconName) => {
    const found = ICON_OPTIONS.find(o => o.value === iconName);
    return found ? found.icon : Sparkles;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Visite Guidée
            </h1>
            <p className="text-slate-500">
              Configurez le tutoriel interactif pour vos clients, membres et apporteurs
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
              <span className="text-sm text-slate-600">Visite guidée</span>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
              <span className={cn("text-sm font-medium", settings.enabled ? "text-green-600" : "text-slate-400")}>
                {settings.enabled ? 'Activée' : 'Désactivée'}
              </span>
            </div>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              style={{ backgroundColor: theme.colors?.accent }}
              className="text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Tabs for each role */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-lg">
            <TabsTrigger value="client" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Clients</span>
            </TabsTrigger>
            <TabsTrigger value="team_member" className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              <span className="hidden sm:inline">Membres</span>
            </TabsTrigger>
            <TabsTrigger value="affiliate" className="flex items-center gap-2">
              <HandCoins className="w-4 h-4" />
              <span className="hidden sm:inline">Apporteurs</span>
            </TabsTrigger>
          </TabsList>

          {['client', 'team_member', 'affiliate'].map((role) => (
            <TabsContent key={role} value={role} className="mt-6">
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>{getRoleLabel(role)}</CardTitle>
                    <CardDescription>
                      {settings.tours[role].length} étape(s) configurée(s)
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetToDefaults(role)}
                    >
                      Réinitialiser
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => addStep(role)}
                      style={{ backgroundColor: theme.colors?.accent }}
                      className="text-white"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {settings.tours[role].length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucune étape configurée</p>
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => addStep(role)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Ajouter une étape
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {settings.tours[role].map((step, index) => {
                        const IconComp = getIconComponent(step.icon);
                        return (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors"
                          >
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => moveStep(role, index, -1)}
                                disabled={index === 0}
                                className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"
                              >
                                <GripVertical className="w-4 h-4 text-slate-400" />
                              </button>
                            </div>
                            
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                              style={{ backgroundColor: `${theme.colors?.accent}15` }}
                            >
                              <IconComp className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  Étape {index + 1}
                                </Badge>
                                {step.videoUrl && (
                                  <Badge className="bg-red-100 text-red-700 text-xs">
                                    <Play className="w-3 h-3 mr-1" />
                                    Vidéo
                                  </Badge>
                                )}
                              </div>
                              <h4 className="font-medium mt-1 truncate">{step.title}</h4>
                              <p className="text-sm text-slate-500 truncate">{step.description}</p>
                            </div>
                            
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => editStep(role, index)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteStep(role, index)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Step Edit Modal */}
        <Dialog open={showStepModal} onOpenChange={setShowStepModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingStep?.isNew ? 'Nouvelle étape' : 'Modifier l\'étape'}
              </DialogTitle>
            </DialogHeader>
            
            {editingStep && (
              <div className="space-y-4">
                <div>
                  <Label>Icône</Label>
                  <Select
                    value={editingStep.icon}
                    onValueChange={(v) => setEditingStep({ ...editingStep, icon: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        return (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" />
                              {opt.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Titre *</Label>
                  <Input
                    value={editingStep.title}
                    onChange={(e) => setEditingStep({ ...editingStep, title: e.target.value })}
                    placeholder="Ex: Bienvenue sur votre Dashboard"
                  />
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={editingStep.description}
                    onChange={(e) => setEditingStep({ ...editingStep, description: e.target.value })}
                    placeholder="Décrivez cette fonctionnalité..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label>URL Vidéo YouTube (optionnel)</Label>
                  <Input
                    value={editingStep.videoUrl || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, videoUrl: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Collez un lien YouTube pour ajouter une vidéo explicative
                  </p>
                </div>

                {editingStep.videoUrl && (
                  <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden">
                    <iframe
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${editingStep.videoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1] || ''}`}
                      title="Preview"
                      frameBorder="0"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStepModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={saveStep}
                style={{ backgroundColor: theme.colors?.accent }}
                className="text-white"
              >
                {editingStep?.isNew ? 'Ajouter' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default GuidedTourSettingsPage;
